// Application settings (U6) — legacy settings/AppSettingsComponent.vue.
//
// POST settings  : flat mps_settings keys. `loyalty` and `auto_update_time` are
//                  sent as OBJECTS (SettingController@store json_encodes them —
//                  sending JSON strings double-encoded them). quick_cash is "a|b|c".
// PUT settings   : mail + payment .env values (sent together, like legacy).
// POST settings/logo (multipart `logo`), POST settings/barcode {scale_barcode}.

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Barcode, Card as CardIcon, Image, Save2, Setting2, Sms } from 'iconsax-reactjs';

import { adminApi, apiErrorMessage, fetchSettings, fetchSelectOptions, saveEnvSettings, saveSettings } from '../api/client';
import { useAuth } from '../components/AuthGate';
import { ErrorBlock, LoadingBlock } from '../components/StateBlock';
import { t } from '../i18n';
import { WorkspaceFieldGrid, WorkspaceSection } from '../workspace/WorkspaceSection';
import { fetchStates } from '../features/shell/api';
import { UI } from '../brand/ui';

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

type Option = { value: string | number; label: string };

type SettingsResponse = {
  accounts?: Option[];
  customers?: Option[];
  categories?: Option[];
  countries?: Option[];
  timezones?: string[];
  mail_settings?: Record<string, string | null>;
  payment_settings?: Record<string, string | boolean | null>;
  settings?: Record<string, unknown>;
};

type Loyalty = {
  staff: { spent: number | null; points: number | null };
  customer: { spent: number | null; points: number | null };
  equivalence: { points: number | null; amount: number | null };
};

type ScaleBarcode = {
  type: 'weight' | 'price';
  total_digits: number | null;
  flag_digits: number | null;
  item_code_start: number | null;
  item_code_digits: number | null;
  weight_start: number | null;
  weight_digits: number | null;
  weight_divide_by: number | null;
  price_start: number | null;
  price_digits: number | null;
  price_divide_by: number | null;
};

type AutoUpdateTime = { day: string; time: [string, string]; [key: string]: unknown };

const EMPTY_LOYALTY: Loyalty = {
  staff: { spent: null, points: null },
  customer: { spent: null, points: null },
  equivalence: { points: null, amount: null }
};

const EMPTY_SCALE: ScaleBarcode = {
  type: 'weight',
  total_digits: null,
  flag_digits: null,
  item_code_start: null,
  item_code_digits: null,
  weight_start: null,
  weight_digits: null,
  weight_divide_by: null,
  price_start: null,
  price_digits: null,
  price_divide_by: null
};

const LANGUAGES = ['en', 'fr', 'es', 'de'];
const QUICK_CASH = [10, 20, 50, 100, 500, 1000, 5000, 10000, 20000, 50000, 100000];
const DIMENSION_UNITS = ['mm', 'cm', 'in', 'foot', 'yd', 'm'];
const WEIGHT_UNITS = ['g', 'kg', 'oz', 'lb', 't'];
const WEEK_DAYS = ['mondays', 'tuesdays', 'wednesdays', 'thursdays', 'fridays', 'saturdays', 'sundays'];
const CARD_GATEWAYS = ['', 'Stripe', 'PayPal_Pro', 'PayPal_Rest', 'AuthorizeNetApi_Api'];
const MAILERS = ['smtp', 'sendmail', 'mailgun'];

// Checkbox settings (true-value "1", false-value "0" like legacy).
const GENERAL_FLAGS = [
  'hide_id',
  'stock',
  'impersonation',
  'play_sound',
  'onscreen_keyboard',
  'pos_server',
  'auto_open_order',
  'print_dialog',
  'categories_sidebar',
  'restaurant',
  'require_country'
];
const ORDER_FLAGS = ['show_image', 'show_tax', 'show_tax_summary', 'show_discount'];

const REQUIRED = [
  'name',
  'short_name',
  'company',
  'email',
  'phone',
  'address',
  'merchant_id',
  'timezone',
  'language',
  'inventory_accounting',
  'theme',
  'loader',
  'rows',
  'default_account',
  'default_customer',
  'default_category',
  'country',
  'state',
  'reference',
  'max_discount'
];

// Keys the label designer / scale barcode dialog own (never echoed by the main form).
const NOT_FORM_KEYS = ['svg_string', 'json_string', 'scale_barcode', 'label_width', 'label_height', 'loyalty', 'quick_cash', 'auto_update_time'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const str = (v: unknown) => (v == null ? '' : String(v));
const flag = (v: unknown) => v === true || v === 1 || v === '1' || v === 'true';

function numOrNull(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function parseJson<T>(raw: unknown): T | null {
  if (raw && typeof raw === 'object') return raw as T;
  if (typeof raw !== 'string' || !raw) return null;
  try {
    // Values double-encoded by the previous React form decode to a string first.
    let parsed: unknown = JSON.parse(raw);
    if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    return parsed && typeof parsed === 'object' ? (parsed as T) : null;
  } catch {
    return null;
  }
}

function buildLoyalty(raw: unknown): Loyalty {
  const src = parseJson<Partial<Loyalty>>(raw);
  if (!src) return structuredClone(EMPTY_LOYALTY);
  return {
    staff: { spent: numOrNull(src.staff?.spent), points: numOrNull(src.staff?.points) },
    customer: { spent: numOrNull(src.customer?.spent), points: numOrNull(src.customer?.points) },
    equivalence: { points: numOrNull(src.equivalence?.points), amount: numOrNull(src.equivalence?.amount) }
  };
}

function loyaltyIsEmpty(l: Loyalty) {
  return [l.staff, l.customer, l.equivalence].every((group) => Object.values(group).every((v) => v == null));
}

/** Pure serializer for `POST settings` (exported for verification). */
export function serializeSettings(form: Record<string, unknown>, loyalty: Loyalty, quickCash: number[], autoUpdateTime: AutoUpdateTime | null) {
  const payload: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(form)) if (!NOT_FORM_KEYS.includes(key)) payload[key] = value;
  payload.quick_cash = [...quickCash].sort((a, b) => a - b).join('|');
  if (!loyaltyIsEmpty(loyalty)) payload.loyalty = loyalty;
  if (autoUpdateTime) payload.auto_update_time = autoUpdateTime;
  return payload;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function SettingsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const [accounts, setAccounts] = useState<Option[]>([]);
  const [customers, setCustomers] = useState<Option[]>([]);
  const [categories, setCategories] = useState<Option[]>([]);
  const [countries, setCountries] = useState<Option[]>([]);
  const [states, setStates] = useState<Option[]>([]);
  const [merchants, setMerchants] = useState<Option[]>([]);
  const [timezones, setTimezones] = useState<string[]>([]);

  const [form, setForm] = useState<Record<string, unknown>>({});
  const [loyalty, setLoyalty] = useState<Loyalty>(structuredClone(EMPTY_LOYALTY));
  const [quickCash, setQuickCash] = useState<number[]>([]);
  const [autoUpdateTime, setAutoUpdateTime] = useState<AutoUpdateTime | null>(null);
  const [logo, setLogo] = useState<string | null>(null);

  const [mail, setMail] = useState<Record<string, string>>({});
  const [payment, setPayment] = useState<Record<string, string | boolean>>({});
  const [scale, setScale] = useState<ScaleBarcode>({ ...EMPTY_SCALE });
  const [dialog, setDialog] = useState<null | 'mail' | 'payment' | 'barcode' | 'logo'>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [data, merchantOptions] = await Promise.all([
          fetchSettings() as Promise<SettingsResponse>,
          fetchSelectOptions('merchants/search').catch(() => [] as Option[])
        ]);
        if (cancelled) return;
        setAccounts(data.accounts ?? []);
        setCustomers(data.customers ?? []);
        setCategories(data.categories ?? []);
        setCountries(data.countries ?? []);
        setTimezones(data.timezones?.length ? data.timezones : Intl.supportedValuesOf('timeZone'));
        setMerchants(merchantOptions);
        setMail(Object.fromEntries(Object.entries(data.mail_settings ?? {}).map(([k, v]) => [k, str(v)])));
        const pay = Object.fromEntries(Object.entries(data.payment_settings ?? {}).map(([k, v]) => [k, str(v)])) as Record<string, string | boolean>;
        pay.PAYPAL_ENABLED = flag(data.payment_settings?.PAYPAL_ENABLED);
        setPayment(pay);

        const s = { ...(data.settings ?? {}) };
        setLoyalty(buildLoyalty(s.loyalty));
        setQuickCash(
          str(s.quick_cash)
            .split('|')
            .map(Number)
            .filter((n) => n && !Number.isNaN(n))
        );
        setAutoUpdateTime(parseJson<AutoUpdateTime>(s.auto_update_time));
        const storedScale = parseJson<Partial<ScaleBarcode>>(s.scale_barcode);
        if (storedScale) setScale({ ...EMPTY_SCALE, ...storedScale });
        setLogo(str(s.default_logo) || null);
        s.inventory_accounting = s.inventory_accounting || 'FIFO';
        s.search_delay = s.search_delay ?? 250;
        // The merchant select is required by the backend (store() reads it).
        s.merchant_id = s.merchant_id || (user as { merchant_id?: string } | null)?.merchant_id || merchantOptions[0]?.value || '';
        setForm(s);
      } catch (err) {
        if (!cancelled) setError(apiErrorMessage(err, t('common.unableToLoad')));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const country = str(form.country);
  useEffect(() => {
    if (!country) return setStates([]);
    fetchStates(country)
      .then(setStates)
      .catch(() => setStates([]));
  }, [country]);

  const setField = (key: string, value: unknown) => setForm((prev) => ({ ...prev, [key]: value }));

  const errors = useMemo(() => {
    const out: Record<string, string> = {};
    for (const key of REQUIRED) if (form[key] == null || form[key] === '') out[key] = t('settings.required');
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(str(form.email))) out.email = t('settings.invalidEmail');
    const delay = Number(form.search_delay);
    if (Number.isNaN(delay) || delay < 200 || delay > 1000) out.search_delay = t('settings.searchDelayRange', { min: 200, max: 1000 });
    if (quickCash.length < 3 || quickCash.length > 5) out.quick_cash = t('settings.quickCashRange');
    return out;
  }, [form, quickCash]);
  const err = (key: string) => (showErrors ? errors[key] : undefined);

  const handleSave = async () => {
    if (Object.keys(errors).length) {
      setShowErrors(true);
      setToast({ msg: t('common.fixHighlighted'), sev: 'error' });
      return;
    }
    setSaving(true);
    try {
      const saved = await saveSettings(serializeSettings(form, loyalty, quickCash, autoUpdateTime ?? { day: 'saturdays', time: ['03:00', '22:00'] }));
      if (saved?.auto_update_time) setAutoUpdateTime(parseJson<AutoUpdateTime>(saved.auto_update_time));
      setToast({ msg: t('settings.saved'), sev: 'success' });
    } catch (e) {
      setToast({ msg: apiErrorMessage(e, t('common.saveFailed')), sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingBlock />;
  if (error) return <ErrorBlock message={error} />;

  const text = (key: string, label: string, props: Record<string, unknown> = {}) => (
    <TextField
      label={label}
      required={REQUIRED.includes(key)}
      value={str(form[key])}
      error={Boolean(err(key))}
      helperText={err(key)}
      onChange={(e) => setField(key, e.target.value)}
      {...props}
    />
  );

  const select = (key: string, label: string, options: Option[], props: Record<string, unknown> = {}) => {
    const value = str(form[key]);
    const opts = value && !options.some((o) => String(o.value) === value) ? [{ value, label: value }, ...options] : options;
    return (
      <TextField select label={label} required={REQUIRED.includes(key)} value={value} error={Boolean(err(key))} helperText={err(key)} onChange={(e) => setField(key, e.target.value)} {...props}>
        {!REQUIRED.includes(key) ? <MenuItem value="">{t('common.none')}</MenuItem> : null}
        {opts.map((o) => (
          <MenuItem key={String(o.value)} value={String(o.value)}>
            {o.label}
          </MenuItem>
        ))}
      </TextField>
    );
  };

  const radios = (key: string, label: string, options: Option[]) => (
    <Box>
      <FormLabel sx={{ fontWeight: 700, fontSize: 13 }} error={Boolean(err(key))}>
        {label}
      </FormLabel>
      <RadioGroup row value={str(form[key])} onChange={(e) => setField(key, e.target.value)}>
        {options.map((o) => (
          <FormControlLabel key={String(o.value)} value={String(o.value)} control={<Radio size="small" />} label={o.label} />
        ))}
      </RadioGroup>
    </Box>
  );

  const check = (key: string) => (
    <FormControlLabel key={key} control={<Checkbox checked={flag(form[key])} onChange={(e) => setField(key, e.target.checked ? '1' : '0')} />} label={t(`settings.flag.${key}`)} />
  );

  const loyaltyField = (group: keyof Loyalty, key: string, label: string) => (
    <TextField
      label={label}
      type="number"
      value={(loyalty[group] as Record<string, number | null>)[key] ?? ''}
      onChange={(e) => setLoyalty((prev) => ({ ...prev, [group]: { ...prev[group], [key]: numOrNull(e.target.value) } }))}
    />
  );

  const digitOptions = [0, 1, 2, 3, 4].map((n) => ({ value: String(n), label: String(n) }));

  return (
    <Stack spacing={2.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Setting2 size={28} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t('nav.appSettings')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('settings.subtitle')}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Button variant="outlined" startIcon={<Sms size={16} />} onClick={() => setDialog('mail')}>
            {t('settings.mailSettings')}
          </Button>
          <Button variant="outlined" startIcon={<CardIcon size={16} />} onClick={() => setDialog('payment')}>
            {t('settings.paymentSettings')}
          </Button>
          <Button variant="outlined" startIcon={<Barcode size={16} />} onClick={() => setDialog('barcode')}>
            {t('settings.scaleBarcode')}
          </Button>
          <Button variant="outlined" startIcon={<Image size={16} />} onClick={() => setDialog('logo')}>
            {t('settings.changeLogo')}
          </Button>
        </Stack>
      </Stack>

      {showErrors && Object.keys(errors).length ? <Alert severity="warning">{t('common.fixHighlighted')}</Alert> : null}

      <WorkspaceSection title={t('settings.businessIdentity')} description={t('settings.businessIdentity.desc')}>
        <WorkspaceFieldGrid>
          {text('name', t('settings.field.name'))}
          {text('short_name', t('settings.field.shortName'))}
          {text('company', t('settings.field.company'))}
          {text('number', t('settings.field.number'))}
          {text('email', t('settings.field.email'), { type: 'email' })}
          {text('phone', t('settings.field.phone'))}
          {text('address', t('settings.field.address'), { multiline: true, minRows: 2 })}
          {select('merchant_id', t('settings.field.merchant'), merchants)}
          {select('country', t('settings.field.country'), countries, {
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => setForm((prev) => ({ ...prev, country: e.target.value, state: '' }))
          })}
          {select('state', t('settings.field.state'), states, { disabled: !country })}
        </WorkspaceFieldGrid>
      </WorkspaceSection>

      <WorkspaceSection title={t('settings.defaults')} description={t('settings.defaults.desc')}>
        <WorkspaceFieldGrid>
          {select('default_account', t('settings.field.defaultAccount'), accounts)}
          {select('default_category', t('settings.field.defaultCategory'), categories)}
          {select('default_customer', t('settings.field.defaultCustomer'), customers)}
        </WorkspaceFieldGrid>
      </WorkspaceSection>

      <WorkspaceSection title={t('settings.regional')} description={t('settings.regional.desc')}>
        <WorkspaceFieldGrid>
          {select('timezone', t('settings.field.timezone'), timezones.map((tz) => ({ value: tz, label: tz })))}
          {select('language', t('settings.field.language'), LANGUAGES.map((l) => ({ value: l, label: `${t(`settings.lang.${l}`)} (${l})` })))}
          {text('user_locale', t('settings.field.userLocale'), { placeholder: 'fr-FR' })}
          {select('reference', t('settings.field.reference'), ['yearly', 'monthly', 'sequence', 'random'].map((v) => ({ value: v, label: t(`settings.reference.${v}`) })))}
        </WorkspaceFieldGrid>
      </WorkspaceSection>

      <WorkspaceSection title={t('settings.operations')} description={t('settings.operations.desc')}>
        <WorkspaceFieldGrid>
          {select('inventory_accounting', t('settings.field.inventoryAccounting'), ['FIFO', 'LIFO', 'AVCO', 'ITEX'].map((v) => ({ value: v, label: t(`settings.inventory.${v}`) })))}
          {select('overselling', t('settings.field.overselling'), [
            { value: '0', label: t('settings.disable') },
            { value: '1', label: t('settings.enable') }
          ])}
          {text('max_discount', t('settings.field.maxDiscount'), { type: 'number', InputProps: { endAdornment: <InputAdornment position="end">%</InputAdornment> } })}
          {text('search_delay', t('settings.field.searchDelay'), { type: 'number', InputProps: { endAdornment: <InputAdornment position="end">ms</InputAdornment> } })}
          {text('header', t('settings.field.receiptHeader'), { multiline: true, minRows: 3 })}
          {text('footer', t('settings.field.receiptFooter'), { multiline: true, minRows: 3 })}
        </WorkspaceFieldGrid>
      </WorkspaceSection>

      <WorkspaceSection title={t('settings.loyalty')} description={t('settings.loyalty.desc')}>
        <Stack spacing={2}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t('settings.loyalty.customer')}
          </Typography>
          <WorkspaceFieldGrid>
            {loyaltyField('customer', 'spent', t('settings.loyalty.spent'))}
            {loyaltyField('customer', 'points', t('settings.loyalty.points'))}
          </WorkspaceFieldGrid>
          <Divider />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t('settings.loyalty.staff')}
          </Typography>
          <WorkspaceFieldGrid>
            {loyaltyField('staff', 'spent', t('settings.loyalty.spent'))}
            {loyaltyField('staff', 'points', t('settings.loyalty.points'))}
          </WorkspaceFieldGrid>
          <Divider />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t('settings.loyalty.equivalence')}
          </Typography>
          <WorkspaceFieldGrid>
            {loyaltyField('equivalence', 'points', t('settings.loyalty.points'))}
            {loyaltyField('equivalence', 'amount', t('settings.loyalty.amount'))}
          </WorkspaceFieldGrid>
        </Stack>
      </WorkspaceSection>

      <WorkspaceSection title={t('settings.posDisplay')} description={t('settings.posDisplay.desc')}>
        <Stack spacing={2}>
          <Box>
            <FormLabel sx={{ fontWeight: 700, fontSize: 13 }} error={Boolean(err('quick_cash'))}>
              {t('settings.field.quickCash')}
            </FormLabel>
            <Stack direction="row" sx={{ flexWrap: 'wrap' }}>
              {QUICK_CASH.map((amount) => (
                <FormControlLabel
                  key={amount}
                  control={
                    <Checkbox
                      checked={quickCash.includes(amount)}
                      onChange={(e) => setQuickCash((prev) => (e.target.checked ? [...prev, amount] : prev.filter((v) => v !== amount)))}
                    />
                  }
                  label={amount.toLocaleString()}
                />
              ))}
            </Stack>
            <Typography variant="caption" color={err('quick_cash') ? 'error' : 'text.secondary'}>
              {t('settings.quickCashRange')}
            </Typography>
          </Box>
          {radios('dimension_unit', t('settings.field.dimensionUnit'), DIMENSION_UNITS.map((u) => ({ value: u, label: t(`settings.unit.${u}`) })))}
          {radios('weight_unit', t('settings.field.weightUnit'), WEIGHT_UNITS.map((u) => ({ value: u, label: t(`settings.unit.${u}`) })))}
          {radios('theme', t('settings.field.theme'), [
            { value: 'dark', label: t('shell.theme.default') },
            { value: 'light', label: t('shell.theme.light') },
            { value: 'primary', label: t('shell.theme.primary') }
          ])}
          {radios('loader', t('settings.field.loader'), [
            { value: 'circle', label: t('settings.loader.circle') },
            { value: 'dot', label: t('settings.loader.dot') }
          ])}
          {radios('confirmation', t('settings.field.confirmation'), [
            { value: 'modal', label: t('action.yes') },
            { value: '', label: t('settings.confirmation.no') }
          ])}
          {radios('decimals', t('settings.field.decimals'), digitOptions)}
          {radios('quantity_decimals', t('settings.field.quantityDecimals'), digitOptions)}
          {radios('rows', t('settings.field.rows'), ['10', '25', '50', '100'].map((v) => ({ value: v, label: v })))}
          <Divider />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>{GENERAL_FLAGS.map(check)}</Box>
          <Typography variant="overline" color="text.secondary">
            {t('settings.orderSettings')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>{ORDER_FLAGS.map(check)}</Box>
          <Typography variant="overline" color="text.secondary">
            {t('settings.appUpdates')}
          </Typography>
          {check('auto_update')}
          {flag(form.auto_update) ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label={t('settings.autoUpdate.day')}
                value={autoUpdateTime?.day ?? 'saturdays'}
                onChange={(e) => setAutoUpdateTime((prev) => ({ time: ['03:00', '22:00'], ...(prev ?? {}), day: e.target.value }))}
                sx={{ minWidth: 200 }}
              >
                {WEEK_DAYS.map((d) => (
                  <MenuItem key={d} value={d}>
                    {t(`settings.day.${d}`)}
                  </MenuItem>
                ))}
              </TextField>
              {[0, 1].map((index) => (
                <TextField
                  key={index}
                  type="time"
                  label={t(index ? 'settings.autoUpdate.to' : 'settings.autoUpdate.from')}
                  value={autoUpdateTime?.time?.[index] ?? (index ? '22:00' : '03:00')}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: '03:00', max: '20:59' }}
                  onChange={(e) =>
                    setAutoUpdateTime((prev) => {
                      const time: [string, string] = [...(prev?.time ?? ['03:00', '22:00'])] as [string, string];
                      time[index] = e.target.value;
                      return { day: 'saturdays', ...(prev ?? {}), time };
                    })
                  }
                />
              ))}
            </Stack>
          ) : null}
        </Stack>
      </WorkspaceSection>

      <Card sx={{ position: 'sticky', bottom: 16, zIndex: 10 }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Button variant="contained" color="primary" disabled={saving} onClick={() => void handleSave()} startIcon={<Save2 size={18} />} data-testid="save-settings">
            {saving ? t('action.saving') : t('common.saveSettings')}
          </Button>
        </CardContent>
      </Card>

      <EnvDialog
        open={dialog === 'mail' || dialog === 'payment'}
        which={dialog === 'payment' ? 'payment' : 'mail'}
        mail={mail}
        payment={payment}
        setMail={setMail}
        setPayment={setPayment}
        onClose={() => setDialog(null)}
        onToast={setToast}
      />
      <ScaleBarcodeDialog open={dialog === 'barcode'} value={scale} onChange={setScale} onClose={() => setDialog(null)} onToast={setToast} />
      <LogoDialog open={dialog === 'logo'} current={logo} onSaved={setLogo} onClose={() => setDialog(null)} onToast={setToast} />

      <Snackbar open={Boolean(toast)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} autoHideDuration={4000} onClose={() => setToast(null)}>
        {toast ? (
          <Alert severity={toast.sev} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Mail / payment (.env) dialog — PUT settings with both groups, like legacy.
// ---------------------------------------------------------------------------

type ToastFn = (toast: { msg: string; sev: 'success' | 'error' }) => void;

function EnvDialog({
  open,
  which,
  mail,
  payment,
  setMail,
  setPayment,
  onClose,
  onToast
}: {
  open: boolean;
  which: 'mail' | 'payment';
  mail: Record<string, string>;
  payment: Record<string, string | boolean>;
  setMail: (v: Record<string, string>) => void;
  setPayment: (v: Record<string, string | boolean>) => void;
  onClose: () => void;
  onToast: ToastFn;
}) {
  const [saving, setSaving] = useState(false);
  const m = (key: string, label: string, type = 'text') => (
    <TextField key={key} size="small" type={type} label={label} value={mail[key] ?? ''} onChange={(e) => setMail({ ...mail, [key]: e.target.value })} />
  );
  const p = (key: string, label: string, type = 'text') => (
    <TextField key={key} size="small" type={type} label={label} value={String(payment[key] ?? '')} onChange={(e) => setPayment({ ...payment, [key]: e.target.value })} />
  );
  const gateway = String(payment.CARD_GATEWAY ?? '');

  const save = async () => {
    setSaving(true);
    try {
      await saveEnvSettings({ ...mail, ...payment, PAYPAL_ENABLED: payment.PAYPAL_ENABLED ? 1 : 0 });
      onToast({ msg: t('settings.saved'), sev: 'success' });
      onClose();
    } catch (e) {
      onToast({ msg: apiErrorMessage(e, t('common.saveFailed')), sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{which === 'mail' ? t('settings.mailSettings') : t('settings.paymentSettings')}</DialogTitle>
      <DialogContent dividers>
        {which === 'mail' ? (
          <Stack spacing={2}>
            {m('MAIL_FROM_NAME', t('settings.mail.fromName'))}
            {m('MAIL_FROM_ADDRESS', t('settings.mail.fromAddress'), 'email')}
            <TextField select size="small" label={t('settings.mail.driver')} value={mail.MAIL_MAILER ?? ''} onChange={(e) => setMail({ ...mail, MAIL_MAILER: e.target.value })}>
              {MAILERS.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>
            {mail.MAIL_MAILER === 'smtp'
              ? [
                  m('MAIL_HOST', t('settings.mail.host')),
                  m('MAIL_PORT', t('settings.mail.port')),
                  m('MAIL_USERNAME', t('settings.mail.username')),
                  m('MAIL_PASSWORD', t('settings.mail.password'), 'password'),
                  m('MAIL_ENCRYPTION', t('settings.mail.encryption'))
                ]
              : null}
            {mail.MAIL_MAILER === 'mailgun'
              ? [m('MAILGUN_DOMAIN', t('settings.mail.mailgunDomain')), m('MAILGUN_SECRET', t('settings.mail.mailgunSecret'), 'password'), m('MAILGUN_ENDPOINT', t('settings.mail.mailgunEndpoint'))]
              : null}
          </Stack>
        ) : (
          <Stack spacing={2}>
            {p('CURRENCY_CODE', t('settings.payment.currency'))}
            <FormControlLabel
              control={<Checkbox checked={Boolean(payment.PAYPAL_ENABLED)} onChange={(e) => setPayment({ ...payment, PAYPAL_ENABLED: e.target.checked })} />}
              label={t('settings.payment.enablePaypal')}
            />
            {payment.PAYPAL_ENABLED || gateway === 'PayPal_Pro'
              ? [p('PAYPAL_USERNAME', t('settings.payment.paypalUsername')), p('PAYPAL_PASSWORD', t('settings.payment.paypalPassword'), 'password'), p('PAYPAL_SIGNATURE', t('settings.payment.paypalSignature'))]
              : null}
            <TextField select size="small" label={t('settings.payment.cardGateway')} value={gateway} onChange={(e) => setPayment({ ...payment, CARD_GATEWAY: e.target.value })}>
              {CARD_GATEWAYS.map((g) => (
                <MenuItem key={g || 'none'} value={g}>
                  {g ? t(`settings.gateway.${g}`) : t('settings.disable')}
                </MenuItem>
              ))}
            </TextField>
            {gateway === 'Stripe' ? [p('STRIPE_KEY', t('settings.payment.stripeKey')), p('STRIPE_SECRET', t('settings.payment.stripeSecret'), 'password')] : null}
            {gateway === 'PayPal_Rest' ? [p('PAYPAL_CLIENT_ID', t('settings.payment.paypalClientId')), p('PAYPAL_SECRET', t('settings.payment.paypalSecret'), 'password')] : null}
            {gateway === 'AuthorizeNetApi_Api'
              ? [p('AUTHORIZE_LOGIN', t('settings.payment.authorizeLogin')), p('AUTHORIZE_TRANSACTION_KEY', t('settings.payment.authorizeKey'), 'password')]
              : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('action.cancel')}</Button>
        <Button variant="contained" disabled={saving} onClick={() => void save()}>
          {saving ? t('action.saving') : t('action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Scale barcode — POST settings/barcode { scale_barcode }
// ---------------------------------------------------------------------------

function ScaleBarcodeDialog({
  open,
  value,
  onChange,
  onClose,
  onToast
}: {
  open: boolean;
  value: ScaleBarcode;
  onChange: (v: ScaleBarcode) => void;
  onClose: () => void;
  onToast: ToastFn;
}) {
  const [saving, setSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const prefix = value.type === 'price' ? 'price' : 'weight';
  const required = ['total_digits', 'item_code_start', 'item_code_digits', `${prefix}_start`, `${prefix}_digits`, `${prefix}_divide_by`] as Array<keyof ScaleBarcode>;
  const missing = required.filter((key) => value[key] == null);

  const num = (key: keyof ScaleBarcode) => (
    <TextField
      key={key}
      size="small"
      type="number"
      required={required.includes(key)}
      label={t(`settings.scale.${key}`)}
      value={value[key] ?? ''}
      error={showErrors && missing.includes(key)}
      onChange={(e) => onChange({ ...value, [key]: numOrNull(e.target.value) })}
    />
  );

  const save = async () => {
    if (missing.length) return setShowErrors(true);
    setSaving(true);
    try {
      await adminApi.post('settings/barcode', { scale_barcode: { ...value } });
      onToast({ msg: t('settings.saved'), sev: 'success' });
      onClose();
    } catch (e) {
      onToast({ msg: apiErrorMessage(e, t('common.saveFailed')), sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('settings.scaleBarcode')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t('settings.scale.guide')}
          </Typography>
          <WorkspaceFieldGrid>
            <TextField select size="small" required label={t('settings.scale.type')} value={value.type} onChange={(e) => onChange({ ...value, type: e.target.value as ScaleBarcode['type'] })}>
              <MenuItem value="price">{t('settings.scale.price')}</MenuItem>
              <MenuItem value="weight">{t('settings.scale.weight')}</MenuItem>
            </TextField>
            {num('total_digits')}
            {num('item_code_start')}
            {num('item_code_digits')}
            {num(`${prefix}_start` as keyof ScaleBarcode)}
            {num(`${prefix}_digits` as keyof ScaleBarcode)}
            {num(`${prefix}_divide_by` as keyof ScaleBarcode)}
          </WorkspaceFieldGrid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('action.cancel')}</Button>
        <Button variant="contained" disabled={saving} onClick={() => void save()} data-testid="save-barcode">
          {saving ? t('action.saving') : t('action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Logo — POST settings/logo (multipart `logo`, jpg/png/svg ≤ 300 KB)
// ---------------------------------------------------------------------------

function LogoDialog({
  open,
  current,
  onSaved,
  onClose,
  onToast
}: {
  open: boolean;
  current: string | null;
  onSaved: (url: string) => void;
  onClose: () => void;
  onToast: ToastFn;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const tooLarge = Boolean(file && file.size > 300 * 1024);

  useEffect(() => {
    if (!file) return setPreview(null);
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const save = async () => {
    if (!file || tooLarge) return;
    setSaving(true);
    try {
      const body = new FormData();
      body.append('logo', file);
      const response = await adminApi.post('settings/logo', body);
      if (response.data?.success && response.data.logo) onSaved(String(response.data.logo));
      onToast({ msg: t('settings.logoSaved'), sev: 'success' });
      setFile(null);
      onClose();
    } catch (e) {
      onToast({ msg: apiErrorMessage(e, t('common.saveFailed')), sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('settings.changeLogo')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} alignItems="center">
          <Avatar variant="rounded" src={preview ?? current ?? undefined} sx={{ width: 160, height: 80, bgcolor: UI.accentAlpha(0.06) }} imgProps={{ style: { objectFit: 'contain' } }}>
            <Image size={28} />
          </Avatar>
          <Button component="label" variant="outlined">
            {t('settings.chooseLogo')}
            <input hidden type="file" accept="image/png,image/jpeg,image/svg+xml" data-testid="logo-input" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </Button>
          <Typography variant="caption" color={tooLarge ? 'error' : 'text.secondary'}>
            {t('settings.logoHint')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('action.cancel')}</Button>
        <Button variant="contained" disabled={saving || !file || tooLarge} onClick={() => void save()}>
          {saving ? t('action.saving') : t('action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
